import './MonitorsTable.scss';

import { AppSelect } from '@ui/applications';
import { AsyncStateNotification } from '@ui/notifications';
import { CreateMonitorModal } from './MonitorModal';
import { getColumns } from './MonitorsTableColumns';
import { monitorParentType } from './MonitorTypeTag';
import { MonitorTypeFilter } from './MonitorTypeFilter';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { RecommendedActionTemplates } from './RecommendedActionTemplates';
import { SonarText } from '@ui/text';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { SortedBySelect } from '@ui/table-modifier/SortedBy';

import { Button, Flex, Popover, Space } from 'antd';
import {
	COPY_GENERIC,
	COPY_SPECIFIC,
	PLACEHOLDERS_COPY,
	SETTINGS_DATA_SECURITY_COPY
} from '@sonar-software/sonar-ts-constants';
import {
	coreActions,
	DataSecurityMonitor,
	MonitorFormTypes,
	RecommendedActionTemplateDetails
} from '@sonar-software/sonar-core';
import { createAsyncSelectors, createSonarSelector } from '@lib/selectors';
import { DynamicQueryOperator, SonarV3SortDirection, SortByOption } from '@sonar-software/sonar-ts-types';
import { FullUserBoundary, SearchInput, SonarCard } from '@ui/general';
import { getMenuItems, monitorMenuConfigs } from './monitor-form/MonitorMenu';
import { PaginatedTable, PaginatedTableQueryParams } from '@ui/tables/PaginatedTable';
import React, { useCallback, useMemo, useState } from 'react';
import {
	useAction,
	useAsyncState,
	useBoolean,
	useCurrentUser,
	useFeatureFlags,
	useOnMount,
	useQueryParams,
	useStakeholders
} from '@hooks';

const getRowKey = (result: DataSecurityMonitor) => result.id;


const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'name',
		label: COPY_SPECIFIC.NAME
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'monitorTypeId',
		label: COPY_SPECIFIC.TYPE
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'applicationId',
		label: COPY_SPECIFIC.APP
	}
];

const dataSecurityMonitorsSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.get);
const postMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.post);
const deleteMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.byId.delete);
const subscribeMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.byId.subscribe);
const muteMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.byId.mute);

export type MonitorsTableQueryParams = PaginatedTableQueryParams & {
	deleted?: boolean;
	applicationId?: number;
	name?: string;
	typeIds?: number[];
};

const convertToDql = (params: MonitorsTableQueryParams) => queryParamsToDynamicQueryLanguageParams(params, {
	filterParams: {
		applicationId: true,
		deleted: true,
		name: { operator: DynamicQueryOperator.Like },
		typeIds: {
			name: 'monitorTypeId',
			operator: DynamicQueryOperator.In
		}
	}
});

export const MonitorsTable: React.FC = () => {
	const { id, isReadOnly } = useCurrentUser();
	const stakeholders = useStakeholders();
	const muteMonitor = useAction(coreActions.requestPatchMuteCoreDataSecurityMonitorsById);
	const subscribeMonitor = useAction(coreActions.requestPostSubscribeCoreDataSecurityMonitorsById);
	const getDataSecurityMonitors = useAction(coreActions.requestGetCoreDataSecurityMonitors);
	const deleteMonitor = useAction(coreActions.requestDeleteCoreDataSecurityMonitorsById);
	const { data, isRequested } = useAsyncState(dataSecurityMonitorsSelectors);
	const [ showModal, setShowModal ] = useBoolean(false);
	const [ showPopover, setShowPopover ] = useBoolean(false);
	const [ editMonitor, setEditMonitor ] = useState<MonitorFormTypes | undefined>(undefined);
	const { isEnabled } = useFeatureFlags();

	const userStakeholderId = useMemo(
		() => stakeholders.find((stakeholder) => stakeholder.userId === id)?.stakeholderId,
		[ id, stakeholders ]
	);

	const menuItemsCount = useMemo(
		() => (data?.results ? getMenuItems(data?.results ?? [], isEnabled)?.length : 0),
		[ data?.results, isEnabled ]
	);

	const columns = useMemo(
		() => getColumns(
			isReadOnly,
			Number(userStakeholderId),
			(mute, monitorId) => muteMonitor({
				id: monitorId,
				isMuted: mute
			}),
			(subscribe, monitorId) => subscribeMonitor({
				id: monitorId,
				subscribe: subscribe
			})
		), [ isReadOnly, muteMonitor, subscribeMonitor, userStakeholderId ]
	);

	const { queryParams, updateParams } = useQueryParams<MonitorsTableQueryParams>({
		defaultParams: {
			deleted: false,
			sortBy: sortOptions[ 1 ].key,
			sortDirection: sortOptions[ 0 ].direction as SonarV3SortDirection
		},
		onUpdate: (newParams, prev, combined) => {
			const { key, paginationDirection } = newParams;

			const params = convertToDql({
				...combined,
				key,
				paginationDirection
			});

			if (params.ordering?.includes('MonitorTypeId')) {
				const [ , direction ] = params.ordering.split('~');
				params.ordering = `Configurable~${direction} ${params.ordering}`;
			}

			getDataSecurityMonitors(params);
		}
	});

	useOnMount(() => {
		updateParams({});
	});

	const handleSortUpdate = useCallback((update: SortByFilterQueryParams) => {
		updateParams(update as PaginatedTableQueryParams);
	}, [ updateParams ]);

	const handleNameChange = useCallback((name: string) => {
		updateParams({ name });
	}, [ updateParams ]);

	const handleTypeSelect = useCallback((typeId: number) => {
		if (typeId === undefined) {
			updateParams({ typeIds: [] });

			return;
		}

		const childTypes = Object.keys(
			Object.fromEntries(
				Object.entries(monitorParentType).filter(([ , value ]) => value === typeId)
			)
		);

		const typeIds = childTypes.length === 0 ?
			[ typeId ] :
			childTypes.map(Number);
		updateParams({ typeIds });
	}, [ updateParams ]);

	const handleAppSelect = useCallback((applicationId: number) => {
		updateParams({ applicationId });
	}, [ updateParams ]);

	const handleDeleteMonitor = useCallback((monitor: DataSecurityMonitor) => {
		deleteMonitor({ monitorId: monitor.id });
	}, [ deleteMonitor ]);

	const handleEditMonitor = useCallback((monitor: DataSecurityMonitor) => {
		setEditMonitor(monitor);
		setShowModal.on();
	}, [ setShowModal ]);

	const handleOpenModal = useCallback(() => {
		setEditMonitor(undefined);
		setShowModal.on();
	}, [ setShowModal ]);

	const handleOnTemplateChange = useCallback((templateDetails: RecommendedActionTemplateDetails) => {
		const newMonitor = {
			applicationIds: templateDetails.integratedApplicationIds ?? [],
			monitorConfig: {
				...templateDetails.configuration
			},
			overrideCount: templateDetails.overrideCount,
			templateId: templateDetails.id
		} as MonitorFormTypes;

		setEditMonitor(newMonitor);
		setShowModal.on();
	}, [ setShowModal ]);

	return (
		<>
			<SonarCard
				className='monitors-table-card'
				extra={
					<FullUserBoundary>
						<Popover
							content={
								<Space
									className='upsell-popover'
									direction='vertical'
									wrap>
									<SonarText weight='semibold'>
										{ COPY_SPECIFIC.CREATE_MONITOR_UPSELL_TITLE }
									</SonarText>
									<SonarText>{ COPY_SPECIFIC.CREATE_MONITOR_UPSELL_BODY }</SonarText>
								</Space>
							}
							onOpenChange={ (value) => {
								if (menuItemsCount === 0 && value) {
									setShowPopover.on();
								} else {
									setShowPopover.off();
								}
							} }
							open={ showPopover }
							placement='bottom'
							rootClassName='monitor-upsell-popover'>
							<Button
								disabled={ menuItemsCount === 0 }
								onClick={ handleOpenModal }
								type='primary'>
								{ COPY_SPECIFIC.CREATE_MONITOR }
							</Button>
						</Popover>
					</FullUserBoundary>
				}
				noSpacing
				title={ COPY_SPECIFIC.MONITORS }>
				<RecommendedActionTemplates onClick={ handleOnTemplateChange } />
				<Flex
					align='end'
					className='filter-section'
					justify='space-between'>
					<div className='table-filters'>
						<Space direction='vertical'>
							<SonarText>{ COPY_SPECIFIC.NAME }</SonarText>
							<SearchInput
								className='monitor-name-input'
								onChange={ handleNameChange }
								placeholder={ PLACEHOLDERS_COPY.SEARCH_MONITORS } />
						</Space>
						<MonitorTypeFilter onTypeSelect={ handleTypeSelect } />
						<Space direction='vertical'>
							<SonarText>{ COPY_SPECIFIC.APP }</SonarText>
							<AppSelect
								nameAndLabel
								onChange={ handleAppSelect }
								placeholder={ PLACEHOLDERS_COPY.FILTER_BY(COPY_SPECIFIC.APP) } />
						</Space>
					</div>
					<SortedBySelect
						defaultValue={ sortOptions[ 0 ] }
						onChange={ handleSortUpdate }
						queryParams={ queryParams as SortByFilterQueryParams }
						sortOptions={ sortOptions } />
				</Flex>
				<PaginatedTable
					canDeleteRow={ (record) =>
						Boolean(monitorMenuConfigs.find(
							(config) => config.type === record.monitorConfig?.monitorTypeId
						)) && !monitorMenuConfigs.find(
							(config) => config.type === record.monitorConfig?.monitorTypeId
						)?.intrinsic }
					canEditRow={ (record) =>
						Boolean(monitorMenuConfigs.find(
							(config) => config.type === record.monitorConfig?.monitorTypeId
						)) }
					columns={ columns }
					id='monitors-table'
					loading={ isRequested }
					onChange={ updateParams }
					onDelete={ handleDeleteMonitor }
					onEdit={ handleEditMonitor }
					resource={ data }
					rowKey={ getRowKey }
					scroll={ {
						scrollToFirstRowOnChange: true,
						x: 'max-content'
					} }
					sortBy={ queryParams.sortBy }
					sortDirection={ queryParams.sortDirection }
					sticky />
				<CreateMonitorModal
					monitor={ editMonitor }
					onCancel={ setShowModal.off }
					onChange={ setEditMonitor }
					onSave={ setShowModal.off }
					open={ showModal } />
			</SonarCard>
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_SAVED }
				resetRawAction={ coreActions.resetPostCoreDataSecurityMonitors }
				stateSelector={ postMonitorSelectors } />
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_DELETED }
				resetRawAction={ coreActions.resetDeleteCoreDataSecurityMonitorsById }
				stateSelector={ deleteMonitorSelectors } />
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_SAVED }
				resetRawAction={ coreActions.resetPostSubscribeCoreDataSecurityMonitorsById }
				stateSelector={ subscribeMonitorSelectors } />
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_SAVED }
				resetRawAction={ coreActions.resetPatchMuteCoreDataSecurityMonitorsById }
				stateSelector={ muteMonitorSelectors } />
		</>
	);
};
